import React, { useState, useEffect, useRef } from 'react';
import { Search, Plus, Edit2, Trash2, X, Award, CheckCircle2, AlertCircle, Upload, Image as ImageIcon } from 'lucide-react';
import { AdminService, apiFetch } from '../../services/api';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8001';

const uploadImage = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('module_id', '1');
  formData.append('pathLocation', 'SPECIALITY_IMAGES/');
  const response = await apiFetch(`${API_BASE_URL}/admin/awsS3FileUpload`, {
    method: 'POST',
    body: formData,
  });
  if (!response.ok) throw new Error(`Upload failed: ${response.statusText}`);
  const json = await response.json();
  return json.privatePathUrl || json.url || json.filePath || '';
};

const SpecialityMaster = () => {
  const [specialities, setSpecialities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSpeciality, setEditingSpeciality] = useState(null);
  const [formData, setFormData] = useState({
    speciality: '',
    speciality_filename: '',
    upload_banner: '',
  });
  const [iconPreview, setIconPreview] = useState(null);
  const [iconUploading, setIconUploading] = useState(false);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [bannerUploading, setBannerUploading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });

  const iconInputRef = useRef(null);
  const bannerInputRef = useRef(null);

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: '', message: '' }), 4000);
  };

  const fetchSpecialities = async () => {
    setLoading(true);
    try {
      const response = await AdminService.getAllSpecialities();
      if (response && response.status === 1 && Array.isArray(response.data)) {
        setSpecialities(response.data);
      } else {
        setSpecialities([]);
      }
    } catch (err) {
      showAlert('error', `Failed to load specialities: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSpecialities(); }, []);

  const resetForm = () => {
    setFormData({ speciality: '', speciality_filename: '', upload_banner: '' });
    setIconPreview(null);
    setBannerPreview(null);
  };

  const handleOpenAdd = () => {
    setEditingSpeciality(null);
    resetForm();
    setModalOpen(true);
  };

  const handleOpenEdit = (item) => {
    setEditingSpeciality(item);
    setFormData({
      speciality: item.speciality || '',
      speciality_filename: item.speciality_filename || '',
      upload_banner: item.upload_banner || '',
    });
    setIconPreview(item.speciality_filename || null);
    setBannerPreview(item.upload_banner || null);
    setModalOpen(true);
  };

  const handleIconUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIconPreview(URL.createObjectURL(file));
    setIconUploading(true);
    try {
      const url = await uploadImage(file);
      setFormData(prev => ({ ...prev, speciality_filename: url }));
    } catch (err) {
      showAlert('error', `Icon upload failed: ${err.message}`);
      setIconPreview(formData.speciality_filename || null);
    } finally {
      setIconUploading(false);
    }
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBannerPreview(URL.createObjectURL(file));
    setBannerUploading(true);
    try {
      const url = await uploadImage(file);
      setFormData(prev => ({ ...prev, upload_banner: url }));
    } catch (err) {
      showAlert('error', `Banner upload failed: ${err.message}`);
      setBannerPreview(formData.upload_banner || null);
    } finally {
      setBannerUploading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete speciality "${name}"?`)) return;
    try {
      const response = await AdminService.deleteSpeciality(id);
      if (response && response.status === 1) {
        showAlert('success', response.msg || 'Speciality deleted successfully');
        fetchSpecialities();
      } else {
        showAlert('error', response?.msg || 'Cannot delete speciality');
      }
    } catch (err) {
      showAlert('error', err.message || 'Error deleting speciality');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.speciality.trim()) {
      showAlert('error', 'Speciality name is required');
      return;
    }
    if (!formData.speciality_filename) {
      showAlert('error', 'Icon image is required');
      return;
    }
    if (iconUploading || bannerUploading) {
      showAlert('error', 'Please wait for uploads to finish');
      return;
    }

    setActionLoading(true);
    try {
      let response;
      if (editingSpeciality) {
        response = await AdminService.updateSpeciality(editingSpeciality.id, formData);
      } else {
        response = await AdminService.addSpeciality(formData);
      }
      if (response && response.status === 1) {
        showAlert('success', editingSpeciality ? 'Speciality updated successfully' : 'Speciality added successfully');
        setModalOpen(false);
        resetForm();
        fetchSpecialities();
      } else {
        showAlert('error', response?.msg || 'Failed to save speciality');
      }
    } catch (err) {
      showAlert('error', err.message || 'Error saving speciality');
    } finally {
      setActionLoading(false);
    }
  };

  const filtered = specialities.filter((item) =>
    (item.speciality || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="content-section">
      <div className="content-header">
        <div>
          <h1>Speciality Master</h1>
          <p className="text-xs text-slate-400 font-medium mt-1">Manage doctor medical specialities</p>
        </div>
        <div className="header-actions">
          <div className="search-container">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search specialities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="create-btn" onClick={handleOpenAdd}>
            <Plus size={18} /> Add Speciality
          </button>
        </div>
      </div>

      {alert.show && (
        <div className={`mb-6 p-4 rounded-xl text-sm font-semibold flex items-center gap-3 ${alert.type === 'success' ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
          {alert.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
          <span>{alert.message}</span>
        </div>
      )}

      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <Award size={48} className="mx-auto mb-3 text-slate-300" />
          <h3>No Specialities Found</h3>
          <p>Click "Add Speciality" to register a new medical speciality.</p>
        </div>
      ) : (
        <div className="cards-grid">
          {filtered.map((item) => (
            <div key={item.id} className="admin-mgmt-card group">
              <div className="admin-mgmt-card-header">
                <div className="flex items-center gap-3.5">
                  {item.speciality_filename ? (
                    <img
                      src={item.speciality_filename}
                      alt={item.speciality}
                      className="w-12 h-12 rounded-xl object-contain bg-slate-50 p-1.5 border border-slate-100 shadow-sm"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center font-bold text-lg border border-teal-100 shadow-sm">
                      <Award size={22} />
                    </div>
                  )}
                  <div>
                    <h3 className="text-base font-bold text-slate-800 leading-snug">{item.speciality}</h3>
                    <span className="text-[11px] text-slate-400 font-medium">ID: #{item.id}</span>
                  </div>
                </div>
                <span className="status-pill approved">
                  <span className="dot" />
                  Active
                </span>
              </div>

              <div className="admin-mgmt-card-body">
                {item.upload_banner && (
                  <div className="mt-1 mb-2">
                    <img
                      src={item.upload_banner}
                      alt="Banner"
                      className="w-full h-20 rounded-xl object-cover border border-slate-100"
                    />
                  </div>
                )}
              </div>

              <div className="admin-mgmt-card-footer flex justify-between items-center pt-3 border-t border-slate-100">
                <span className="text-xs text-slate-400">Doctor Category</span>
                <div className="flex items-center gap-2">
                  <button onClick={() => handleOpenEdit(item)} className="p-2 rounded-lg text-slate-500 hover:text-teal-600 hover:bg-teal-50 transition-all" title="Edit Speciality">
                    <Edit2 size={16} />
                  </button>
                  <button onClick={() => handleDelete(item.id, item.speciality)} className="p-2 rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-50 transition-all" title="Delete Speciality">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-100 relative animate-fadeIn" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
            <button onClick={() => { setModalOpen(false); resetForm(); }} className="absolute top-4 right-4 p-1.5 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
              <X size={18} />
            </button>

            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center border border-teal-100 shadow-sm">
                <Award size={20} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-800 leading-tight">
                  {editingSpeciality ? 'Edit Speciality' : 'Add New Speciality'}
                </h3>
                <p className="text-xs text-slate-500 font-medium">Medical domain category</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Speciality Name */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Speciality Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.speciality}
                  onChange={(e) => setFormData({ ...formData, speciality: e.target.value })}
                  placeholder="e.g. Cardiology, Orthopedics"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-600/10 font-medium"
                  required
                />
              </div>

              {/* Icon Image Upload */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Icon Image <span className="text-red-500">*</span>
                </label>
                <div
                  onClick={() => iconInputRef.current?.click()}
                  className="cursor-pointer border-2 border-dashed rounded-xl p-4 flex items-center gap-4 transition-all hover:border-teal-400 hover:bg-teal-50"
                  style={{ borderColor: iconPreview ? '#0d9488' : '#e2e8f0' }}
                >
                  {iconPreview ? (
                    <img src={iconPreview} alt="Icon preview" className="w-16 h-16 rounded-xl object-contain bg-slate-50 border border-slate-200 p-1" />
                  ) : (
                    <div className="w-16 h-16 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <ImageIcon size={24} className="text-slate-400" />
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-bold text-slate-700">
                      {iconUploading ? 'Uploading...' : iconPreview ? 'Click to change icon' : 'Click to upload icon'}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">PNG, JPG, SVG recommended · Max 10MB</p>
                    {iconUploading && (
                      <div className="mt-1.5 h-1.5 w-32 bg-slate-200 rounded-full overflow-hidden">
                        <div className="h-full bg-teal-500 rounded-full animate-pulse" style={{ width: '60%' }} />
                      </div>
                    )}
                  </div>
                </div>
                <input ref={iconInputRef} type="file" accept="image/*" onChange={handleIconUpload} className="hidden" />
              </div>

              {/* Banner Image Upload */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Banner Image <span className="text-xs text-slate-400 normal-case font-medium">(Optional)</span>
                </label>
                <div
                  onClick={() => bannerInputRef.current?.click()}
                  className="cursor-pointer border-2 border-dashed rounded-xl overflow-hidden transition-all hover:border-teal-400"
                  style={{ borderColor: bannerPreview ? '#0d9488' : '#e2e8f0' }}
                >
                  {bannerPreview ? (
                    <div className="relative">
                      <img src={bannerPreview} alt="Banner preview" className="w-full h-28 object-cover" />
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                        <p className="text-white text-xs font-bold flex items-center gap-1"><Upload size={14} /> Change Banner</p>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <Upload size={18} className="text-slate-400" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-slate-700">{bannerUploading ? 'Uploading...' : 'Click to upload banner'}</p>
                        <p className="text-xs text-slate-400">Wide image for speciality banner</p>
                      </div>
                    </div>
                  )}
                </div>
                <input ref={bannerInputRef} type="file" accept="image/*" onChange={handleBannerUpload} className="hidden" />
              </div>

              <div className="pt-2 flex items-center justify-end gap-2.5">
                <button type="button" onClick={() => { setModalOpen(false); resetForm(); }} className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 text-xs font-bold transition-all">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading || iconUploading || bannerUploading}
                  className="px-5 py-2.5 rounded-xl bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold transition-all shadow-md shadow-teal-600/20 disabled:opacity-50"
                >
                  {actionLoading ? 'Saving...' : editingSpeciality ? 'Update Speciality' : 'Add Speciality'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpecialityMaster;
